/**
 * Banker panel — Gold -> Cash exchange, one-way. Modeled directly on
 * vendor-ui.ts's factory-function pattern (create / open / close / dispose,
 * `.panel.grained` + backdrop-click + Escape to close).
 *
 * ─── The control ────────────────────────────────────────────────────────────
 * The amount is a SLIDER whose ceiling is the player's whole Gold balance,
 * with a typed field beside it for an exact figure and percentage chips for
 * the four amounts anyone actually picks. It used to be a bare number input
 * with `max = balance?.gold ?? 999999` — a fallback that let the panel offer
 * an amount the server would reject, because the balance was not read until
 * after the first exchange. `banker-state.ts` seeds it from the character
 * sheet now, and this file refuses to draw a slider without it: an unknown
 * ceiling is an undrawable control, not a guessable one.
 *
 * ─── Why the render is split in two ─────────────────────────────────────────
 * `renderStructure` builds the DOM; `patch` writes the numbers into it. The
 * store is subscribed to `economy:updated` socket pushes, so a single
 * rebuild-everything render would recreate the range input underneath a
 * player mid-drag and drop their pointer capture. Same split, same reasoning,
 * as `currency-shop-ui.ts`'s `patchCheckout` — including its rule that
 * `slider.value` and the typed field are only written when they are NOT the
 * focused element, so nothing fights the player's own input.
 *
 * ─── The confirm step ───────────────────────────────────────────────────────
 * The one UI in this codebase with a confirm-before-committing step: no
 * reusable confirm/modal component exists anywhere yet (checked), so this is
 * a small second overlay built inline rather than a new shared abstraction
 * for a single caller. Everywhere else that mutates (paperdoll's Equip/
 * Store/Discard, the vendor/currency-shop Buy/Sell buttons) fires immediately
 * — this one doesn't, because it's a real-money-adjacent, no-undo trade
 * (Gold is otherwise deposit-only; this is the one place it leaves an
 * account). The slider strengthens that case rather than weakening it: MAX is
 * now one click away, so the whole balance can leave in two gestures.
 *
 * `pendingAmount` is local module state, not part of `BankerState` — it is
 * purely "what the confirm overlay is asking about right now", never
 * persisted, never read outside this file.
 */
import {
  bankerStateStore, loadBanker, exchange, closeBanker,
} from '../../state/banker-state';
import type { BankerState } from '../../state/banker-state.types';
import { closeOtherModals, registerPanel } from './panels';
import {
  cashAmount, createWindowChrome, goldAmount, windowHeading,
} from './window-chrome';

let unsubscribe: (() => void) | null = null;
let onKeyDown: ((e: KeyboardEvent) => void) | null = null;
let pendingAmount: number | null = null;

/**
 * The chosen amount, in Gold. Module state rather than store state for the
 * same reason `pendingAmount` is: it is what this panel's controls are
 * currently showing, not something any other screen reads. Clamped against
 * the live balance on every patch — after an exchange the ceiling drops, and
 * a stale 500 must come down with it.
 */
let inputValue = 1;

/**
 * What `renderStructure` last built for, so `patch` knows when the DOM it is
 * writing into still exists. Only these three transitions change the shape of
 * the panel; every other update is numbers.
 */
let structuralKey = '';

/** Deregisters this panel from `ui/panels.ts` — see `registerPanel`. */
let unregisterPanel: (() => void) | null = null;

/** Fractions the chips offer. MAX is the point of the exercise; the rest are the usual thirds. */
const QUICK_FRACTIONS = [0.25, 0.5, 0.75, 1] as const;

/**
 * Grouped digits, for every figure this panel SHOWS.
 *
 * Gold balances here run to seven digits, and `65498250 Cash` beside
 * `2619930 Gold` is two walls of digits that cannot be compared at a glance —
 * which is the entire job of the deal line. Found by looking at a screenshot of
 * a real account, not by a test: every unit test in this file uses two- and
 * three-digit fixtures where the difference does not show.
 *
 * Display only. The typed field stays raw — an input that rewrites what you
 * typed into groups while you type it is fighting you, and its own handler
 * strips everything but digits anyway.
 */
function grouped(value: number): string {
  return value.toLocaleString('en-US');
}

export function createBankerUI(): HTMLElement {
  // One shell for every window — see `ui/window-chrome.ts`. Wider than the
  // 480 it was: the deal line (give -> receive) and the actions row below the
  // track both want horizontal room, and at 480 the chips wrapped under the
  // typed field on every screen rather than only on a narrow one.
  const { root, header, body } = createWindowChrome({
    id: 'banker-ui',
    headerId: 'banker-header',
    bodyId: 'banker-body',
    width: 620,
    label: 'Close banker',
    onClose: () => closeBankerUI(),
  });
  body.classList.add('bv-banker-body');

  // Confirm overlay — sits above the banker panel, inside the same root so
  // Escape/backdrop-click on it don't also fall through to close the panel
  // underneath (see its own keydown/pointerdown handling below).
  const confirmOverlay = document.createElement('div');
  confirmOverlay.id = 'banker-confirm';
  confirmOverlay.className = 'bv-scrim bv-banker-confirm';
  confirmOverlay.style.display = 'none';
  const confirmPanel = document.createElement('div');
  confirmPanel.className = 'panel grained bv-win';
  // 440, not the 380 it opened at: at 380 a seven-digit figure wrapped its own
  // unit onto a second line on both sides of the arrow, which turned the one
  // line a player is meant to read before committing into four.
  confirmPanel.style.setProperty('--win-w', '440px');
  confirmOverlay.appendChild(confirmPanel);
  root.appendChild(confirmOverlay);

  confirmOverlay.addEventListener('pointerdown', (e) => {
    if (e.target === confirmOverlay) cancelConfirm(confirmOverlay);
  });

  onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    if (confirmOverlay.style.display !== 'none') {
      e.preventDefault();
      cancelConfirm(confirmOverlay);
    } else if (root.style.display !== 'none') {
      e.preventDefault();
      closeBankerUI();
    }
  };
  document.addEventListener('keydown', onKeyDown);

  const rerender = () => {
    if (root.style.display === 'none') return;
    render(header, body, confirmOverlay, confirmPanel, bankerStateStore.get());
  };
  unsubscribe = bankerStateStore.subscribe(rerender);


  // One registry instead of the five hand-copied guard chains index.ts
  // used to carry. See ui/panels.ts.
  unregisterPanel?.();
  unregisterPanel = registerPanel({ id: 'banker', isOpen: isBankerUIOpen, close: closeBankerUI });
  return root;
}

export function openBankerUI(bankerKey: string): void {
  closeOtherModals('banker');
  const el = document.getElementById('banker-ui');
  if (el) el.style.display = 'flex';
  pendingAmount = null;
  inputValue = 1;
  structuralKey = '';
  void loadBanker(bankerKey);
}

export function isBankerUIOpen(): boolean {
  const el = document.getElementById('banker-ui');
  return !!el && el.style.display !== 'none';
}

export function closeBankerUI(): void {
  const el = document.getElementById('banker-ui');
  if (el) el.style.display = 'none';
  const confirmEl = document.getElementById('banker-confirm');
  if (confirmEl) confirmEl.style.display = 'none';
  pendingAmount = null;
  structuralKey = '';
  closeBanker();
}

export function disposeBankerUI(): void {
  unregisterPanel?.();
  unregisterPanel = null;
  unsubscribe?.();
  unsubscribe = null;
  if (onKeyDown) document.removeEventListener('keydown', onKeyDown);
  onKeyDown = null;
  structuralKey = '';
}

function cancelConfirm(confirmOverlay: HTMLElement): void {
  pendingAmount = null;
  confirmOverlay.style.display = 'none';
}

// ─── rendering ──────────────────────────────────────────────────────────────

/**
 * Three shapes, and the difference between the last two matters:
 *
 *  - `waiting`  the banker (or the wallet) has not resolved. No controls — a
 *               slider drawn here would have to invent a ceiling.
 *  - `empty`    the wallet resolved and it holds no Gold. Controls are drawn
 *               and disabled, with the reason said out loud, rather than
 *               omitted — a missing control reads as a broken panel.
 *  - `ready`    1..gold.
 *
 * A range input with `min=1 max=0` clamps differently across browsers, which
 * is the other reason `empty` is its own shape rather than `ready` with a
 * degenerate range.
 */
function shapeOf(state: BankerState): 'waiting' | 'empty' | 'ready' {
  if (!state.banker || !state.balance) return 'waiting';
  return state.balance.gold < 1 ? 'empty' : 'ready';
}

function render(
  header: HTMLElement,
  body: HTMLElement,
  confirmOverlay: HTMLElement,
  confirmPanel: HTMLElement,
  state: BankerState,
): void {
  const shape = shapeOf(state);
  const key = `${state.banker?.key ?? ''}|${shape}`;
  if (key !== structuralKey) {
    structuralKey = key;
    renderStructure(body, confirmOverlay, confirmPanel, state, shape);
  }
  renderHeader(header, state, shape);
  patch(body, state, shape);
}

/**
 * The header is rebuilt every time — it is four short lines of text with no
 * focusable control in it, so there is nothing for a rebuild to interrupt.
 * The body is the half that must not be.
 */
function renderHeader(header: HTMLElement, state: BankerState, shape: ReturnType<typeof shapeOf>): void {
  if (!state.banker) {
    header.innerHTML = state.error
      ? `<p class="bv-win-err">${esc(state.error)}</p>`
      : `<p class="bv-win-empty">${state.busy ? 'Loading…' : 'Banker unavailable.'}</p>`;
    return;
  }

  // Cash in bone with its unit spelled, Gold in brass behind the coin — the
  // same two treatments every other window uses. This screen exchanges one for
  // the other, so telling them apart at a glance is the whole job.
  const wallet = state.balance
    ? `${cashAmount(grouped(state.balance.cash))}<span class="bv-banker-dot">·</span>${goldAmount(grouped(state.balance.gold))}`
    : `${cashAmount('—')}<span class="bv-banker-dot">·</span>${goldAmount('—')}`;

  header.innerHTML = `
    ${state.error ? `<p class="bv-win-err">${esc(state.error)}</p>` : ''}
    <div class="bv-banker-head">
      <div>
        ${windowHeading(state.banker.name, 'Exchanges Gold for Cash.')}
      </div>
      <div class="bv-banker-wallet">
        <span class="bv-banker-wallet-label">Your wallet</span>
        <span class="bv-banker-wallet-figs">${wallet}</span>
      </div>
    </div>
    <p class="bv-banker-rate">
      <span class="bv-banker-rate-label">Rate</span>
      <span class="bv-banker-rate-eq">1 Gold = ${state.banker.rate} Cash</span>
      <span class="bv-banker-oneway">One-way</span>
    </p>
    ${state.lastReceipt ? `<p class="bv-win-ok">${esc(state.lastReceipt)}</p>` : ''}
    ${shape === 'waiting' && !state.error ? `<p class="bv-win-empty">${state.busy ? 'Reading your wallet…' : 'Wallet unavailable.'}</p>` : ''}
  `;
}

/**
 * Builds the body once per shape. Every value written here is a placeholder —
 * `patch` owns the numbers, so this function can be read as "the layout" and
 * nothing else.
 */
function renderStructure(
  body: HTMLElement,
  confirmOverlay: HTMLElement,
  confirmPanel: HTMLElement,
  state: BankerState,
  shape: ReturnType<typeof shapeOf>,
): void {
  body.innerHTML = '';
  if (shape === 'waiting') return;

  const max = Math.max(1, state.balance?.gold ?? 1);

  const deal = document.createElement('div');
  deal.className = 'bv-banker-deal';
  /*
   * ─── The layout ───────────────────────────────────────────────────────────
   *   DEAL     what you give and what you get, side by side with the arrow
   *            between them. The two figures are the biggest things on the
   *            panel because they are the only two the decision is made on.
   *   TRACK    the slider, edge to edge, its bounds above it and the running
   *            Gold figure riding under the thumb.
   *   ACTIONS  the typed field and the percentage chips packed left, Exchange
   *            pushed hard right. Left to right that is how much -> shortcuts
   *            -> commit.
   *
   * Same three bands as the Gold shop's checkout, deliberately: they are the
   * same transaction shape (pick an amount on a slider, confirm it), and two
   * money windows that behave alike should look alike.
   */
  deal.innerHTML = `
    <div class="bv-banker-side">
      <span class="bv-banker-side-label">You give</span>
      <span class="bv-banker-give" data-give></span>
    </div>
    <span class="bv-banker-arrow" aria-hidden="true">&rarr;</span>
    <div class="bv-banker-side bv-banker-side-get">
      <span class="bv-banker-side-label">You receive</span>
      <span class="bv-banker-get" data-get></span>
    </div>
  `;
  body.appendChild(deal);

  const sliderRow = document.createElement('div');
  sliderRow.className = 'bv-banker-slider-row';
  sliderRow.innerHTML = `
    <div class="bv-banker-track">
      <!-- The bounds sit ABOVE the ends of the track, not beside it: the thumb
           label below is centred on the thumb, so at either extreme it lands
           exactly where a beside-the-track bound would be. -->
      <span class="bv-banker-bound bv-banker-bound-lo">1</span>
      <span class="bv-banker-bound bv-banker-bound-hi" data-bound-max></span>
      <input type="range" class="bv-banker-slider" data-gold-slider
        min="1" max="${max}" step="1" value="1"
        aria-label="Gold to exchange" />
      <!-- Follows the thumb. Positioned from JS because no CSS expression can
           read a range input's thumb offset. -->
      <span class="bv-banker-thumb-label" data-thumb-label></span>
    </div>
  `;
  body.appendChild(sliderRow);

  const actions = document.createElement('div');
  actions.className = 'bv-banker-actions';
  actions.innerHTML = `
    <label class="bv-banker-field">
      <span class="bv-banker-label">Gold</span>
      <span class="bv-banker-box">
        <!-- No aria-label: the wrapping label element already names this one,
             and the slider carries that exact name — two identically-named
             controls is what a screen reader would have read out. -->
        <input type="text" inputmode="numeric" class="bv-banker-input"
          data-gold-input autocomplete="off" spellcheck="false" />
      </span>
    </label>
    <div class="bv-banker-quick" data-quick-row>
      ${QUICK_FRACTIONS.map((f) => `
        <button type="button" class="bv-banker-chip" data-quick="${f}">
          ${f === 1 ? 'MAX' : `${Math.round(f * 100)}%`}
        </button>`).join('')}
    </div>
    <div class="bv-banker-commit">
      <button type="button" class="bv-act bv-act-go bv-banker-go" data-exchange>Exchange</button>
    </div>
  `;
  body.appendChild(actions);

  const note = document.createElement('p');
  note.className = 'bv-win-note';
  note.textContent = shape === 'empty'
    ? 'No Gold to exchange. Gold is bought in the Gold shop — it has no other source.'
    : 'Cash cannot be turned back into Gold. Exchange only what you mean to spend.';
  body.appendChild(note);

  wireBody(body, confirmOverlay, confirmPanel);
}

/**
 * Every listener the body needs, attached once per structural build.
 *
 * Delegated from the body rather than bound per control: the chips are
 * rebuilt as a group and binding each one individually is how a rebuild
 * quietly loses a handler.
 */
function wireBody(body: HTMLElement, confirmOverlay: HTMLElement, confirmPanel: HTMLElement): void {
  const slider = body.querySelector<HTMLInputElement>('[data-gold-slider]');
  const field = body.querySelector<HTMLInputElement>('[data-gold-input]');

  // `repaint` rather than a direct DOM write: everything that moves with the
  // amount (both figures, the chips, the thumb label, the button text) is
  // written in ONE place, and `patch` is careful not to write back into
  // whichever control is focused — so driving the drag through the store does
  // not fight the thumb.
  slider?.addEventListener('input', () => {
    setAmount(Number(slider.value));
    repaint();
  });

  // On `input`, not `change`: the field and the slider mirror each other while
  // typing. It is clamped on commit rather than per keystroke, so clearing the
  // box to retype does not snap it to 1 under the caret — same discipline as
  // the Gold shop's amount field.
  field?.addEventListener('input', () => {
    const typed = Number(field.value.replace(/[^\d]/g, ''));
    if (!Number.isFinite(typed) || typed <= 0) return;
    setAmount(typed);
    repaint();
  });
  field?.addEventListener('blur', () => {
    setAmount(inputValue);
    repaint();
  });

  body.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;

    const chip = target?.closest<HTMLElement>('[data-quick]');
    if (chip) {
      const gold = bankerStateStore.get().balance?.gold ?? 0;
      const fraction = Number(chip.dataset.quick);
      // Floor, then at least 1: 25% of 3 Gold is 0 and an action that offers
      // to exchange nothing is worse than one that offers the minimum.
      setAmount(Math.max(1, Math.floor(gold * fraction)));
      repaint();
      return;
    }

    if (target?.closest('[data-exchange]')) {
      const state = bankerStateStore.get();
      if (!state.banker || !state.balance || state.balance.gold < 1) return;
      openConfirm(confirmOverlay, confirmPanel, inputValue, state.banker.rate);
    }
  });
}

/** Clamps into 1..balance and stores it. The single writer of `inputValue`. */
function setAmount(gold: number): void {
  const max = bankerStateStore.get().balance?.gold ?? 1;
  const wanted = Number.isFinite(gold) ? Math.floor(gold) : 1;
  inputValue = Math.min(Math.max(1, wanted), Math.max(1, max));
}

/** A store no-op, purely to drive a repaint from a local (non-store) change. */
function repaint(): void {
  bankerStateStore.set((s) => ({ ...s }));
}

/**
 * Writes the numbers into the structure. Never rebuilds, never writes into a
 * control the player is currently using.
 */
function patch(body: HTMLElement, state: BankerState, shape: ReturnType<typeof shapeOf>): void {
  if (shape === 'waiting' || !state.banker) return;

  const gold = state.balance?.gold ?? 0;
  const max = Math.max(1, gold);
  // The ceiling moves: after an exchange the balance drops, and an amount
  // chosen against the old one has to come down with it.
  setAmount(inputValue);
  const amount = inputValue;
  const empty = shape === 'empty';

  // The unit is spelled on BOTH sides here. goldAmount keeps "Gold"
  // screen-reader-only because the coin says it at HUD size — but at this size,
  // opposite a figure that ends in the word "Cash", a bare number reads as a
  // quantity of nothing.
  setHtml(body, '[data-give]', `${goldAmount(grouped(empty ? 0 : amount))}<span class="bv-banker-unit" aria-hidden="true">Gold</span>`);
  setHtml(body, '[data-get]', cashAmount(grouped(empty ? 0 : amount * state.banker.rate)));
  // On the empty shape the range is a formality (a 1..0 input clamps
  // differently per browser, so it is built as 1..1) — printing "1" at both
  // ends under a panel that says there is nothing to exchange reads as a
  // contradiction. Print the real balance: 0.
  setText(body, '[data-bound-max]', grouped(empty ? 0 : max));

  const slider = body.querySelector<HTMLInputElement>('[data-gold-slider]');
  if (slider) {
    if (slider.max !== String(max)) slider.max = String(max);
    // Only pushed IN when the change came from somewhere else — writing
    // `value` during a drag fights the browser's own thumb tracking.
    if (document.activeElement !== slider && slider.value !== String(amount)) {
      slider.value = String(amount);
    }
    slider.disabled = empty || state.busy;
  }

  const field = body.querySelector<HTMLInputElement>('[data-gold-input]');
  if (field) {
    // Not while they are typing in it: rewriting the value under the caret
    // moves it and fights every keystroke.
    if (document.activeElement !== field && field.value !== String(amount)) {
      field.value = String(amount);
    }
    field.disabled = empty || state.busy;
  }

  for (const chip of body.querySelectorAll<HTMLButtonElement>('[data-quick]')) {
    chip.disabled = empty || state.busy;
    // The chip that names the amount currently chosen reads as selected, so
    // the row doubles as a readout of which shortcut you are standing on.
    const fraction = Number(chip.dataset.quick);
    const chipAmount = Math.max(1, Math.floor(gold * fraction));
    chip.classList.toggle('bv-banker-chip-on', !empty && chipAmount === amount);
  }

  // The Gold figure under the thumb. `(value - 1) / (max - 1)` is the
  // fraction; the `(0.5 - frac) * thumb` inset keeps the label centred on the
  // thumb rather than running off the ends of the track, which a plain
  // percentage does at both extremes.
  const label = body.querySelector<HTMLElement>('[data-thumb-label]');
  if (label) {
    const frac = max <= 1 ? 0 : (amount - 1) / (max - 1);
    label.textContent = grouped(empty ? 0 : amount);
    label.style.left = `calc(${(frac * 100).toFixed(3)}% + ${(0.5 - frac).toFixed(3)} * var(--bank-thumb))`;
  }

  const go = body.querySelector<HTMLButtonElement>('[data-exchange]');
  if (go) {
    go.disabled = empty || state.busy;
    // Deliberately NOT "Exchange <n> Gold". The amount is already the biggest
    // thing on the panel and it is named again in the confirm step, while a
    // seven-digit balance made the button grow and shrink under the cursor on
    // every step of a drag.
    go.textContent = state.busy ? 'Working…' : 'Exchange';
  }
}

function setText(root: HTMLElement, selector: string, value: string): void {
  const el = root.querySelector<HTMLElement>(selector);
  if (el && el.textContent !== value) el.textContent = value;
}

function setHtml(root: HTMLElement, selector: string, value: string): void {
  const el = root.querySelector<HTMLElement>(selector);
  if (el && el.innerHTML !== value) el.innerHTML = value;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]!);
}

function openConfirm(confirmOverlay: HTMLElement, confirmPanel: HTMLElement, amount: number, rate: number): void {
  pendingAmount = amount;
  const cash = amount * rate;
  confirmPanel.innerHTML = `
    <h3 class="bv-win-title">Confirm exchange</h3>
    <p class="bv-banker-confirm-line">
      <span class="bv-banker-confirm-give">${goldAmount(grouped(amount))} Gold</span>
      <span class="bv-banker-arrow" aria-hidden="true">&rarr;</span>
      <span class="bv-banker-confirm-get">${cashAmount(grouped(cash))}</span>
    </p>
    <p class="bv-win-note">This cannot be reversed.</p>
  `;

  const row = document.createElement('div');
  row.className = 'bv-act-row bv-banker-confirm-acts';

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'bv-act bv-act-ghost';
  cancel.textContent = 'Cancel';
  cancel.onclick = () => cancelConfirm(confirmOverlay);
  row.appendChild(cancel);

  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.className = 'bv-act bv-act-go';
  confirm.textContent = 'Confirm';
  confirm.onclick = () => {
    const toExchange = pendingAmount;
    confirmOverlay.style.display = 'none';
    pendingAmount = null;
    if (toExchange !== null) void exchange(toExchange);
  };
  row.appendChild(confirm);

  confirmPanel.appendChild(row);
  confirmOverlay.style.display = 'flex';
}
